import { useDisclosure } from '@chakra-ui/react';
import { QuoteOfferingGroups } from '@monetize/types/app';
import { pluralize } from '@monetize/utils/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { MdAccountTree, MdAdd } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../../api/axios';
import { cpqServiceQueryKeys } from '../../../../../api/cpqService';
import {
  MAlert,
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MIcon,
  MText,
} from '../../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../../components/Monetize/ExpandCollapseButton';
import { QuoteAccountHierarchyModal } from '../../../../../components/Quotes/QuoteAccountHierachyModal';
import {
  canMoveQuoteOfferingBetweenGroups,
  getQuoteOfferingGroupPermissions,
} from '../../../../../utils/quotes';
import { useQuoteContext } from '../../quoteContext';
import { ManageQuoteOfferingGroupsChip } from './ManageQuoteOfferingGroupsChip';
import {
  getManageGroupsTitle,
  MANAGE_GROUPS_MIN_WIDTH,
} from './manageQuoteOfferingGroupsConstants';
import { ManageQuoteOfferingGroupsDropZone } from './ManageQuoteOfferingGroupsDropZone';
import { ManageQuoteOfferingGroupsRow } from './ManageQuoteOfferingGroupsRow';
import { countManageGroupsDraftChanges } from './manageQuoteOfferingGroupsUtils';
import {
  ManageGroupsSaveFailure,
  useApplyQuoteOfferingGroupsDraft,
} from './useApplyQuoteOfferingGroupsDraft';
import { useManageQuoteOfferingGroupsDraft } from './useManageQuoteOfferingGroupsDraft';

const EDIT_SUBTITLE =
  'Add groups, set their accounts and contacts, and move offerings between them. Nothing is saved until you choose Save Changes.';
const READ_ONLY_SUBTITLE =
  'The groups on this quote and the offerings in each. This quote is no longer editable.';

interface ManageQuoteOfferingGroupsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Bulk workbench for a quote's offering groups: every group on one screen, every
 * change staged locally, one save.
 *
 * Available on every quote status and type. On a quote that can't be edited it
 * becomes a review view — no edit affordances and no draft-state verbiage, since
 * there is no draft to report on.
 */
export const ManageQuoteOfferingGroupsModal = ({
  isOpen,
  onClose,
}: ManageQuoteOfferingGroupsModalProps) => {
  const {
    quoteData: { quote, fetchQuote },
    quoteOfferingGroups,
    isReadOnly,
  } = useQuoteContext();
  const queryClient = useQueryClient();
  const quoteId = quote?.id || '';
  const accountHierarchyModalState = useDisclosure();

  const [failures, setFailures] = useState<ManageGroupsSaveFailure[]>([]);

  const permissions = useMemo(
    () => getQuoteOfferingGroupPermissions({ quote, isReadOnly }),
    [quote, isReadOnly],
  );

  const {
    rows,
    plan,
    rowStatusByKey,
    rowSubtotalByKey,
    rowErrors,
    offeringById,
    offeringDetailById,
    rootQuoteOfferings,
    ungroupedOfferings,
    hasChanges,
    isValid,
    seed,
    reset,
    addRow,
    updateField,
    assignOffering,
    updateCustomFields,
    removeRow,
  } = useManageQuoteOfferingGroupsDraft({
    quote,
    groups: quoteOfferingGroups?.groups,
  });

  const { mutateAsync: applyDraft, isPending } =
    useApplyQuoteOfferingGroupsDraft(quoteId);

  const changeCount = countManageGroupsDraftChanges(plan);

  /**
   * Collapse state per card, keyed by row key. Absent means open, so a group
   * added while the modal is up starts expanded.
   */
  const [rowOpenStates, setRowOpenStates] = useState<Record<string, boolean>>(
    {},
  );

  const handleRowOpenChange = useCallback(
    (key: string, nextIsOpen: boolean) => {
      setRowOpenStates((prev) => ({ ...prev, [key]: nextIsOpen }));
    },
    [],
  );

  const areAllRowsOpen = rows.every(({ key }) => rowOpenStates[key] !== false);

  const setAllRowsOpen = (nextIsOpen: boolean) =>
    setRowOpenStates(
      Object.fromEntries(rows.map(({ key }) => [key, nextIsOpen])),
    );

  const handleSave = async () => {
    setFailures([]);
    try {
      // Individual request failures come back as `failures` rather than throwing;
      // a rejection here means the batch itself broke.
      const { failures: saveFailures } = await applyDraft(plan);

      // Offering rows behind the modal read membership from the groups query, but
      // each offering's own groupId only refreshes with the quote.
      if (quoteId) {
        await fetchQuote(quoteId);
      }

      // No success toast — closing the modal with the groups updated behind it is
      // its own confirmation.
      if (saveFailures.length === 0) {
        onClose();
        return;
      }

      // Some requests landed and some didn't — re-seed from the server so the
      // user edits what actually exists, not a draft that is now half stale.
      setFailures(saveFailures);
      const groupsQueryKey = [
        ...cpqServiceQueryKeys.quoteOfferingGroups(quoteId),
      ];
      await queryClient.refetchQueries({ queryKey: groupsQueryKey });
      seed(
        queryClient.getQueryData<QuoteOfferingGroups>(groupsQueryKey)?.groups,
      );
    } catch (error) {
      handleApiErrorToast(error);
    }
  };

  return (
    <MCenterModal
      size="full"
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      showCloseButton={!isPending}
      modalTitle={getManageGroupsTitle(permissions.isReadOnly)}
      subTitle={permissions.isReadOnly ? READ_ONLY_SUBTITLE : EDIT_SUBTITLE}
      modalBodyProps={{ bg: 'tGray.sidebarDark' }}
      renderFooter={() =>
        permissions.isReadOnly ? (
          <MFlex justify="flex-end" w="full">
            <MButton variant="secondary" onClick={onClose}>
              Close
            </MButton>
          </MFlex>
        ) : (
          <MFlex justify="space-between" align="center" w="full">
            <MText color="fg.muted" fontSize="sm">
              {hasChanges
                ? `${changeCount} unsaved ${pluralize('change', changeCount)}`
                : 'No unsaved changes'}
            </MText>
            <MFlex gap={2}>
              <MButton
                variant="tertiary"
                size="sm"
                onClick={reset}
                isDisabled={!hasChanges || isPending}
                data-testid="manage-groups-reset-btn"
              >
                Reset
              </MButton>
              <MButton
                variant="secondary"
                onClick={onClose}
                isDisabled={isPending}
              >
                Cancel
              </MButton>
              <MButton
                variant="primary"
                onClick={handleSave}
                isLoading={isPending}
                isDisabled={!hasChanges || !isValid}
                data-testid="manage-groups-save-btn"
              >
                Save Changes
              </MButton>
            </MFlex>
          </MFlex>
        )
      }
    >
      <MFlex direction="column" gap={3}>
        {failures.length > 0 && (
          <MAlert type="error" title="Some changes could not be saved">
            <MFlex direction="column">
              {failures.map(({ label, message }) => (
                <MText key={`${label}-${message}`} fontSize="sm">
                  {label}: {message}
                </MText>
              ))}
              <MText fontSize="sm" mt={1}>
                The groups below have been reloaded from the server.
              </MText>
            </MFlex>
          </MAlert>
        )}

        {/*
         * Expand/collapse left, secondary action right — the same arrangement
         * the offering table behind this modal uses.
         */}
        <MFlex justify="space-between" align="center" gap={2}>
          <MBox>
            {rows.length > 1 && (
              <ExpandCollapseButton
                isExpanded={areAllRowsOpen}
                onChange={setAllRowsOpen}
                data-testid="manage-groups-expand-collapse-btn"
              />
            )}
          </MBox>
          {/*
           * The hierarchy is what the Ship To accounts are picked from, so it is
           * reachable from here rather than only from the quote page.
           */}
          <MButton
            variant="tertiary"
            size="sm"
            leftIcon={<MIcon as={MdAccountTree} />}
            onClick={accountHierarchyModalState.onOpen}
            data-testid="manage-groups-view-hierarchy-btn"
          >
            View Account Hierarchy
          </MButton>
        </MFlex>
        {accountHierarchyModalState.open && quote?.accountId && (
          <QuoteAccountHierarchyModal
            accountId={quote.accountId}
            isOpen={accountHierarchyModalState.open}
            onClose={accountHierarchyModalState.onClose}
          />
        )}

        {/* Columns have a floor width; the list scrolls here, never the page */}
        <MBox overflowX="auto">
          <MBox minW={MANAGE_GROUPS_MIN_WIDTH}>
            <MFlex direction="column" gap={3}>
              {rows.map((row, rowIndex) => (
                <ManageQuoteOfferingGroupsRow
                  key={row.key}
                  row={row}
                  rowIndex={rowIndex}
                  rootAccountId={quote?.accountId || ''}
                  offeringById={offeringById}
                  offeringDetailById={offeringDetailById}
                  permissions={permissions}
                  subtotal={rowSubtotalByKey.get(row.key) || ''}
                  status={rowStatusByKey.get(row.key)}
                  errors={rowErrors.get(row.key)}
                  onUpdateField={updateField}
                  onAssignOffering={assignOffering}
                  onUpdateCustomFields={updateCustomFields}
                  onRemoveRow={removeRow}
                  isOpen={rowOpenStates[row.key] !== false}
                  onIsOpenChange={handleRowOpenChange}
                />
              ))}
            </MFlex>
          </MBox>
        </MBox>

        {rows.length === 0 && (
          <MText color="fg.muted" fontSize="sm">
            {permissions.isReadOnly
              ? 'This quote has no offering groups.'
              : 'Add a group to start assigning offerings to different accounts and contacts.'}
          </MText>
        )}

        {permissions.canAddGroup && (
          <MBox>
            <MButton
              variant="tertiary"
              leftIcon={<MIcon as={MdAdd} />}
              onClick={addRow}
              data-testid="manage-groups-add-row-btn"
            >
              Add Group
            </MButton>
          </MBox>
        )}

        <ManageQuoteOfferingGroupsDropZone
          rowKey={null}
          onAssign={assignOffering}
          isDropDisabled={permissions.isReadOnly}
          p={4}
          data-testid="manage-groups-ungrouped-zone"
        >
          <MText fontSize="sm" fontWeight={600} mb={2}>
            Ungrouped
          </MText>
          {ungroupedOfferings.length === 0 ? (
            <MText color="fg.muted" fontSize="sm">
              {rootQuoteOfferings.length === 0
                ? 'This quote has no offerings yet.'
                : 'Every offering is in a group.'}
            </MText>
          ) : (
            <MFlex direction="column" gap={2}>
              {ungroupedOfferings.map((quoteOffering) => (
                <ManageQuoteOfferingGroupsChip
                  key={quoteOffering.id}
                  quoteOffering={quoteOffering}
                  detail={offeringDetailById.get(quoteOffering.id)}
                  rowKey={null}
                  isMovable={canMoveQuoteOfferingBetweenGroups(
                    quoteOffering,
                    permissions,
                  )}
                />
              ))}
            </MFlex>
          )}
        </ManageQuoteOfferingGroupsDropZone>
      </MFlex>
    </MCenterModal>
  );
};
